import { PROJECT_CONFIG } from '../project_branding/config';
import {
  SITE_EVENT_DESTINATIONS,
  resolvePlausibleProps,
  resolveSiteEventDestinations,
  type ResolvedSiteEventDestination,
  type SiteEventDestinationRow,
} from './site-event-destinations';

/**
 * Événements de suivi des SITES CLIENTS (custom-websites) : le contrat public que lisent les
 * agences (dataLayer, événement DOM, Plausible) et les pixels natifs du PROFESSIONNEL. À ne pas
 * confondre avec `tracking/taxonomy.ts`, qui concerne NOS régies (inscription des pros).
 *
 * Règles du contrat (référence : `docs/reference/site-tracking.md`) :
 *  1. aucune marque dans un nom public (rebrand, marque blanche) ;
 *  2. un seul vocabulaire sur toutes les surfaces publiques ;
 *  3. nom GA4 recommandé quand il existe avec le même sens, sinon `objet_action` ;
 *  4. un événement = un fait confirmé, jamais une intention ;
 *  5. paramètres aux conventions GA4 (`value` en unité monétaire, `currency` ISO 4217) ;
 *  6. données personnelles uniquement dans `user_data`, sous consentement marketing ;
 *  7. contrat ADDITIF : un nom publié n'est ni renommé ni retiré (verrouillé par
 *     `site-events.spec.ts`).
 */

export type BuilderMode = 'builder' | 'public' | undefined;

/** Événement DOM émis sur `window`, dont le `detail` est l'objet poussé dans le dataLayer. */
export const SITE_EVENT_DOM_NAME = 'site:event';

/**
 * Tableau du gtag NATIF du site (ID `G-` / `AW-` collé dans les réglages), chargé avec
 * `gtag.js?id=…&l=<nom>`. `window.dataLayer` reste réservé au contrat public et aux conteneurs
 * GTM : GTM traite aussi les `gtag('event', …)` poussés dans le tableau qu'il écoute, un
 * conteneur verrait donc chaque événement deux fois si les deux partageaient `dataLayer`.
 */
export const SITE_GTAG_LAYER_NAME = 'siteGtagLayer';

/** Devise de la plateforme : repli quand le backend n'a pas renvoyé celle du professionnel. */
const SITE_EVENT_DEFAULT_CURRENCY = PROJECT_CONFIG.currency;

/**
 * Devise d'un événement (code ISO 4217 en majuscules) : celle du PROFESSIONNEL, renvoyée par le
 * backend avec les montants. Jamais une constante côté composant : un pro hors devise de la
 * plateforme verrait sa `value` comptée dans la mauvaise devise par les régies.
 */
export function resolveSiteEventCurrency(currency: string | null | undefined): string {
  return currency?.trim().toUpperCase() || SITE_EVENT_DEFAULT_CURRENCY;
}

export const SITE_EVENT_NAMES = [
  'generate_lead',
  'booking_confirmed',
  'purchase',
  'phone_click',
  'email_click',
  'whatsapp_click',
  'newsletter_subscribe',
  'add_to_cart',
  'booking_started',
  'booking_step',
  'booking_slot_selected',
  'booking_prereserved',
  'booking_abandoned',
  'begin_checkout',
] as const;
export type SiteEventName = (typeof SITE_EVENT_NAMES)[number];

/** Garde par la LISTE des noms, pas par la table : `'constructor' in SITE_EVENTS` est vrai. */
export function isSiteEventName(name: unknown): name is SiteEventName {
  return SITE_EVENT_NAMES.some((known): boolean => known === name);
}

export type SiteEventFormType = 'message' | 'callback';
export type SiteEventFormLocation = 'contact_modal' | 'page_block';
export type SiteEventBookingType = 'appointment' | 'ride';
/** Étape du tunnel de réservation affichée au client (la confirmation n'en est pas une). */
export type SiteEventBookingStep = 'services' | 'place' | 'zone_address' | 'member' | 'resource' | 'slot' | 'checkout';
/** Mode du parcours de réservation (spec Parcours § 5) : une prestation connue, le catalogue, plusieurs prestations. */
export type SiteEventBookingEntryMode = 'express' | 'standard' | 'multi';
/** Pourquoi un parcours de réservation s'arrête : pré-réservation expirée, catalogue en erreur, page fermée. */
export type SiteEventBookingAbandonReason = 'expired' | 'error' | 'closed';

export const SITE_EVENT_CLICK_LOCATIONS = ['contact_modal', 'floating_button', 'footer', 'content'] as const;
export type SiteEventClickLocation = (typeof SITE_EVENT_CLICK_LOCATIONS)[number];
/** Attribut posé par les composants : l'ancêtre le plus proche donne le `click_location`. */
export const SITE_EVENT_CLICK_LOCATION_ATTRIBUTE = 'data-track-location';

/** Ligne au format GA4 (`items[]`). `price` en unité monétaire, comme `value`. */
export interface SiteEventItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
}

/** Seul endroit où circulent des données personnelles, et seulement sous consentement marketing. */
export interface SiteEventUserData {
  email?: string;
  /** Format E.164. */
  phone_number?: string;
}

export interface SiteEventParams {
  generate_lead: {
    form_type: SiteEventFormType;
    form_location: SiteEventFormLocation;
    lead_id?: string;
  };
  booking_confirmed: {
    transaction_id: string;
    /** Total TTC des prestations, sur place comprises, bon ou carte cadeau déduit. */
    value: number;
    currency: string;
    /** Débité en ligne aujourd'hui (0 si paiement sur place). */
    amount_paid: number;
    booking_type: SiteEventBookingType;
    service_name: string;
    items: SiteEventItem[];
    /** Une remise (code promo, offre) s'applique à la réservation. */
    promotion?: boolean;
    /** Montant de la remise, en unité monétaire (0 sans remise). */
    discount?: number;
    /** Nom public de l'offre qui remise la réservation ; absent sans promotion. */
    promotion_name?: string;
  };
  purchase: {
    transaction_id: string;
    value: number;
    currency: string;
    items: SiteEventItem[];
  };
  phone_click: {
    /** Numéro du lien cliqué, en E.164 ; absent si le lien ne porte pas un numéro exploitable. */
    phone_number_clicked?: string;
    click_location: SiteEventClickLocation;
  };
  email_click: { click_location: SiteEventClickLocation };
  whatsapp_click: { click_location: SiteEventClickLocation };
  newsletter_subscribe: Record<string, never>;
  add_to_cart: {
    value: number;
    currency: string;
    items: SiteEventItem[];
    /** Offre acquise affichée sur la fiche au moment de l'ajout ; absente sans promotion. */
    promotion_name?: string;
    /** Remise de cette offre sur la ligne ajoutée, en unité monétaire. */
    discount?: number;
  };
  booking_started: {
    /** Prestations déjà dans la sélection à l'arrivée (présélection d'une fiche, panier retrouvé). */
    prestation_count: number;
  };
  booking_step: {
    step: SiteEventBookingStep;
    /** Rang de l'étape, à partir de 1. */
    step_index: number;
    /** Nombre d'étapes du parcours de ce client, confirmation non comptée. */
    step_total: number;
    entry_mode?: SiteEventBookingEntryMode;
  };
  booking_slot_selected: {
    /** Délai entre le choix et le début du rendez-vous, en heures arrondies. */
    lead_time_hours: number;
    promotion: boolean;
  };
  booking_prereserved: {
    promotion: boolean;
  };
  booking_abandoned: {
    last_step: SiteEventBookingStep | 'loading';
    reason: SiteEventBookingAbandonReason;
  };
  begin_checkout: {
    /** Total de la sélection calculé par le serveur, remise comprise. */
    value: number;
    currency: string;
    items: SiteEventItem[];
    promotion: boolean;
    entry_mode?: SiteEventBookingEntryMode;
  };
}

/** Copie des clés e-commerce au format GA4, construite par `trackSiteEvent`, jamais à la main. */
export interface SiteEventEcommerce {
  transaction_id?: string;
  value: number;
  currency: string;
  items: SiteEventItem[];
}

/** Objet poussé dans le dataLayer ET `detail` de l'événement DOM. */
export type SiteEventDetail<T extends SiteEventName = SiteEventName> = { event: T } & SiteEventParams[T] & {
    ecommerce?: SiteEventEcommerce;
    user_data?: SiteEventUserData;
  };

export interface SiteEventDefinition<T extends SiteEventName> {
  /** `false` = signal d'intérêt (ex. `add_to_cart`), pas une conversion. */
  conversion: boolean;
  /** Clés à plat de l'événement, dans l'ordre de la doc. */
  params: readonly (keyof SiteEventParams[T] & string)[];
  /** L'événement peut porter `user_data` (email / téléphone saisis par le visiteur). */
  user_data: boolean;
  /** `transaction_id`, `value`, `currency`, `items` répétés sous `ecommerce` (balises GA4 de GTM). */
  ecommerce: boolean;
  /** Paramètre qui identifie le fait : un même identifiant ne part qu'une fois par navigateur. */
  dedup_key: (keyof SiteEventParams[T] & string) | null;
  /** Une colonne par régie, `null` explicite quand l'événement n'y part pas. */
  destinations: SiteEventDestinationRow<T>;
}

/**
 * LA table : une ligne par événement, une colonne par régie. Ajouter un événement = une ligne
 * ici + une ligne dans `site-event-destinations.ts` + sa description dans `site-events-doc.ts`.
 */
export const SITE_EVENTS: { [K in SiteEventName]: SiteEventDefinition<K> } = {
  generate_lead: {
    conversion: true,
    params: ['form_type', 'form_location', 'lead_id'],
    user_data: true,
    ecommerce: false,
    dedup_key: 'lead_id',
    destinations: SITE_EVENT_DESTINATIONS.generate_lead,
  },
  booking_confirmed: {
    conversion: true,
    params: [
      'transaction_id',
      'value',
      'currency',
      'amount_paid',
      'booking_type',
      'service_name',
      'items',
      'promotion',
      'discount',
      'promotion_name',
    ],
    user_data: true,
    ecommerce: true,
    dedup_key: 'transaction_id',
    destinations: SITE_EVENT_DESTINATIONS.booking_confirmed,
  },
  purchase: {
    conversion: true,
    params: ['transaction_id', 'value', 'currency', 'items'],
    user_data: true,
    ecommerce: true,
    dedup_key: 'transaction_id',
    destinations: SITE_EVENT_DESTINATIONS.purchase,
  },
  phone_click: {
    conversion: true,
    params: ['phone_number_clicked', 'click_location'],
    user_data: false,
    ecommerce: false,
    dedup_key: null,
    destinations: SITE_EVENT_DESTINATIONS.phone_click,
  },
  email_click: {
    conversion: true,
    params: ['click_location'],
    user_data: false,
    ecommerce: false,
    dedup_key: null,
    destinations: SITE_EVENT_DESTINATIONS.email_click,
  },
  whatsapp_click: {
    conversion: true,
    params: ['click_location'],
    user_data: false,
    ecommerce: false,
    dedup_key: null,
    destinations: SITE_EVENT_DESTINATIONS.whatsapp_click,
  },
  newsletter_subscribe: {
    conversion: true,
    params: [],
    user_data: true,
    ecommerce: false,
    dedup_key: null,
    destinations: SITE_EVENT_DESTINATIONS.newsletter_subscribe,
  },
  add_to_cart: {
    conversion: false,
    params: ['value', 'currency', 'items', 'promotion_name', 'discount'],
    user_data: false,
    ecommerce: true,
    dedup_key: null,
    destinations: SITE_EVENT_DESTINATIONS.add_to_cart,
  },
  /* Parcours du tunnel de réservation (où les clients s'arrêtent) : signaux, pas des conversions. */
  booking_started: {
    conversion: false,
    params: ['prestation_count'],
    user_data: false,
    ecommerce: false,
    dedup_key: null,
    destinations: SITE_EVENT_DESTINATIONS.booking_started,
  },
  booking_step: {
    conversion: false,
    params: ['step', 'step_index', 'step_total', 'entry_mode'],
    user_data: false,
    ecommerce: false,
    dedup_key: null,
    destinations: SITE_EVENT_DESTINATIONS.booking_step,
  },
  booking_slot_selected: {
    conversion: false,
    params: ['lead_time_hours', 'promotion'],
    user_data: false,
    ecommerce: false,
    dedup_key: null,
    destinations: SITE_EVENT_DESTINATIONS.booking_slot_selected,
  },
  booking_prereserved: {
    conversion: false,
    params: ['promotion'],
    user_data: false,
    ecommerce: false,
    dedup_key: null,
    destinations: SITE_EVENT_DESTINATIONS.booking_prereserved,
  },
  booking_abandoned: {
    conversion: false,
    params: ['last_step', 'reason'],
    user_data: false,
    ecommerce: false,
    dedup_key: null,
    destinations: SITE_EVENT_DESTINATIONS.booking_abandoned,
  },
  begin_checkout: {
    conversion: false,
    params: ['value', 'currency', 'items', 'promotion', 'entry_mode'],
    user_data: false,
    ecommerce: true,
    dedup_key: null,
    destinations: SITE_EVENT_DESTINATIONS.begin_checkout,
  },
};

/** Événements que le pro peut relier à un libellé de conversion Google Ads (réglages du site). */
export const SITE_CONVERSION_EVENT_NAMES: readonly SiteEventName[] = SITE_EVENT_NAMES.filter(
  (name): boolean => SITE_EVENTS[name].conversion,
);

const ECOMMERCE_KEYS = ['transaction_id', 'value', 'currency', 'items'] as const;

/**
 * Toutes les clés du contrat, remises à `undefined` avant chaque événement : GTM fusionne les
 * push (objets et tableaux, index par index) et garderait sinon la `value`, une ligne d'`items`
 * ou le téléphone d'un événement précédent.
 */
export const SITE_EVENT_CONTRACT_KEYS: readonly string[] = [
  ...new Set<string>(SITE_EVENT_NAMES.flatMap((name): readonly string[] => SITE_EVENTS[name].params)),
  'ecommerce',
  'user_data',
];

/* ── Consentement cookies du site ────────────────────────────────────────────── */

export const SITE_CONSENT_STORAGE_KEY = 'cookie_consent';
export const SITE_CONSENT_CHANGED_EVENT = 'cookie-consent:changed';
/** Poussé dans le dataLayer une fois le choix connu : déclencheur GTM des balises non Google. */
export const SITE_CONSENT_DATA_LAYER_EVENT = 'cookie_consent_update';

/** Durée de validité d'un choix cookies (13 mois, recommandation CNIL). */
export const SITE_CONSENT_MAX_AGE_MS = 13 * 30 * 24 * 60 * 60 * 1000;

export interface SiteCookieConsent {
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

/** Choix du visiteur, `null` tant qu'il n'a pas répondu (ou réponse de plus de 13 mois). */
export function readSiteCookieConsent(): SiteCookieConsent | null {
  try {
    const raw = localStorage.getItem(SITE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SiteCookieConsent;
    if (Date.now() - parsed.timestamp > SITE_CONSENT_MAX_AGE_MS) {
      localStorage.removeItem(SITE_CONSENT_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveSiteCookieConsent(state: SiteCookieConsent): void {
  localStorage.setItem(SITE_CONSENT_STORAGE_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent(SITE_CONSENT_CHANGED_EVENT));
}

/* ── Dédup ───────────────────────────────────────────────────────────────────── */

const DEDUP_STORAGE_KEY = 'site_events_sent';
const DEDUP_MAX_ENTRIES = 50;

/** `true` si ce fait a déjà été émis par ce navigateur ; sinon le mémorise (liste bornée). */
function alreadySent(entry: string): boolean {
  try {
    const raw = localStorage.getItem(DEDUP_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    const sent = Array.isArray(parsed) ? parsed.filter((e): e is string => typeof e === 'string') : [];
    if (sent.includes(entry)) return true;
    localStorage.setItem(DEDUP_STORAGE_KEY, JSON.stringify([...sent, entry].slice(-DEDUP_MAX_ENTRIES)));
    return false;
  } catch {
    // Stockage indisponible (navigation privée stricte) : mieux vaut émettre que perdre le fait.
    return false;
  }
}

/* ── Émission ────────────────────────────────────────────────────────────────── */

/** Montant en unités mineures (centimes) → unité monétaire du contrat (90.0 pour 9000). */
export function minorUnitToSiteEventValue(amountRaw: number): number {
  return Math.round(amountRaw) / 100;
}

const E164_REGEX = /^\+[1-9]\d{6,14}$/;

/** Préfixe interurbain écrit entre parenthèses après l'indicatif : `+33 (0)6 12 34 56 78`. */
const TRUNK_PREFIX_NOTATION = /\(\s*0\s*\)/g;

/**
 * Numéro déjà international → E.164 (séparateurs retirés, `00` → `+`, `(0)` de la notation
 * `+33 (0)6…` retiré : il ne se compose pas depuis l'étranger). Aucun indicatif n'est deviné :
 * `undefined` pour un numéro national. Une saisie libre de visiteur est normalisée par le
 * backend (libphonenumber, pays du pro), pas ici.
 */
export function toE164IfInternational(raw: string | undefined | null): string | undefined {
  const cleaned = (raw ?? '')
    .replace(TRUNK_PREFIX_NOTATION, '')
    .replace(/[^\d+]/g, '')
    .replace(/^00/, '+');
  return E164_REGEX.test(cleaned) ? cleaned : undefined;
}

function cleanUserData(userData: SiteEventUserData | undefined): SiteEventUserData | undefined {
  const email = userData?.email?.trim().toLowerCase();
  const phone = userData?.phone_number?.trim();
  if (!email && !phone) return undefined;
  return { ...(email ? { email } : {}), ...(phone ? { phone_number: phone } : {}) };
}

export interface TrackSiteEventOptions {
  /** Email / téléphone saisis par le visiteur : retirés sans consentement marketing. */
  userData?: SiteEventUserData;
}

/**
 * Construit l'objet d'événement du contrat. Exporté pour les tests : les composants passent par
 * `trackSiteEvent`.
 */
export function buildSiteEventDetail<T extends SiteEventName>(
  name: T,
  params: SiteEventParams[T],
  userData: SiteEventUserData | undefined,
): SiteEventDetail<T> {
  const definition = SITE_EVENTS[name];
  const source = params as Record<string, unknown>;
  const detail: Record<string, unknown> = { event: name };
  for (const key of definition.params) {
    if (source[key] !== undefined) detail[key] = source[key];
  }
  if (definition.ecommerce) {
    const ecommerce: Record<string, unknown> = {};
    for (const key of ECOMMERCE_KEYS) {
      if (detail[key] !== undefined) ecommerce[key] = detail[key];
    }
    detail.ecommerce = ecommerce;
  }
  if (definition.user_data && userData) detail.user_data = userData;
  return detail as SiteEventDetail<T>;
}

/**
 * Émet un événement de site. À appeler quand le fait est CONFIRMÉ (réponse du serveur, clic sur
 * le vrai lien), jamais à l'ouverture d'une fenêtre.
 *
 * Le push dataLayer et l'événement DOM sont faits ici : le contrat public ne dépend d'aucun
 * composant monté ni d'aucun ID configuré. Les pixels natifs sont servis par l'écouteur de
 * `SITE_EVENT_DOM_NAME` (`SiteEventRouter`, custom-websites).
 *
 * @returns `true` si l'événement est parti, `false` s'il a été ignoré (hors site public, doublon).
 */
export function trackSiteEvent<T extends SiteEventName>(
  mode: BuilderMode,
  name: T,
  params: SiteEventParams[T],
  options?: TrackSiteEventOptions,
): boolean {
  if (mode !== 'public') return false;
  if (typeof window === 'undefined') return false;

  const definition = SITE_EVENTS[name];
  if (definition.dedup_key) {
    const id = (params as Record<string, unknown>)[definition.dedup_key];
    if (typeof id === 'string' && id && alreadySent(`${name}:${id}`)) return false;
  }

  const userData = readSiteCookieConsent()?.marketing ? cleanUserData(options?.userData) : undefined;
  const detail = buildSiteEventDetail(name, params, userData);

  const host = window as unknown as { dataLayer?: unknown[] };
  host.dataLayer = host.dataLayer ?? [];
  host.dataLayer.push(Object.fromEntries(SITE_EVENT_CONTRACT_KEYS.map((key): [string, undefined] => [key, undefined])));
  // Copie : GTM annote l'objet poussé (`gtm.uniqueEventId`), le `detail` DOM doit rester le contrat.
  host.dataLayer.push({ ...detail });

  window.dispatchEvent(new CustomEvent<SiteEventDetail<T>>(SITE_EVENT_DOM_NAME, { detail }));
  return true;
}

/* ── Lecture par régie ───────────────────────────────────────────────────────── */

/** Un événement émis, traduit pour chaque régie : liste vide quand il n'y part pas. */
export interface ResolvedSiteEvent {
  ga4: ResolvedSiteEventDestination[];
  google_ads: ResolvedSiteEventDestination[];
  meta: ResolvedSiteEventDestination[];
  tiktok: ResolvedSiteEventDestination[];
  plausible: { name: string; props: Record<string, string | number | boolean> } | null;
}

/**
 * Applique les colonnes de `SITE_EVENTS` à un événement reçu sur `SITE_EVENT_DOM_NAME`.
 * `null` pour un `detail` absent ou un nom hors contrat : un script tiers peut émettre le même
 * événement DOM, avec n'importe quel contenu.
 */
export function resolveSiteEvent<T extends SiteEventName>(
  detail: SiteEventDetail<T> | null | undefined,
): ResolvedSiteEvent | null {
  if (!detail || !isSiteEventName(detail.event)) return null;
  const definition = SITE_EVENTS[detail.event] as SiteEventDefinition<T>;
  const row = definition.destinations;
  return {
    ga4: resolveSiteEventDestinations(row.ga4, detail),
    google_ads: resolveSiteEventDestinations(row.google_ads, detail),
    meta: resolveSiteEventDestinations(row.meta, detail),
    tiktok: resolveSiteEventDestinations(row.tiktok, detail),
    plausible: row.plausible ? { name: row.plausible.name, props: resolvePlausibleProps(row.plausible, detail) } : null,
  };
}
